import React from 'react';
import { Box, Avatar, Typography, SvgIcon } from '@mui/material';
import { palette, spaces, borderRadius, boxShadows } from '../../../assets/styles/themes/tokens';
import getMuiIcon, { type IconName } from '../../../utils/getMuiIcon';
import getTextTruncationSx from '../../../utils/getTextTruncationSx';

export const CHAT_MESSAGE_ARTIFACT_CARD_VARIANT = {
  DOCUMENT: 'document',
  PODCAST: 'podcast',
} as const;

export type ChatMessageArtifactCardVariant =
  (typeof CHAT_MESSAGE_ARTIFACT_CARD_VARIANT)[keyof typeof CHAT_MESSAGE_ARTIFACT_CARD_VARIANT];

// Per-variant differences. The card design is otherwise identical across
// variants — only the leading icon, its size and the default test id change.
// The podcast icon is a play button that reads as a 35px control inside a
// slightly larger box; the whole card remains the click target.
const VARIANT_CONFIG: Record<
  ChatMessageArtifactCardVariant,
  { icon: IconName; defaultTestId: string; iconSize: string; boxSize: string }
> = {
  [CHAT_MESSAGE_ARTIFACT_CARD_VARIANT.DOCUMENT]: {
    icon: 'chatMessageArtifactDocument',
    defaultTestId: 'document-card',
    iconSize: '24px',
    boxSize: '40px',
  },
  [CHAT_MESSAGE_ARTIFACT_CARD_VARIANT.PODCAST]: {
    icon: 'playCircle',
    defaultTestId: 'podcast-card',
    iconSize: '35px',
    boxSize: '44px',
  },
};

export interface ChatMessageArtifactCardProps {
  title: string;
  subtitle?: string;
  onClick?: () => void;
  testId?: string;
  maxWidth?: string;
  isActive?: boolean;
  variant?: ChatMessageArtifactCardVariant;
}

// Compact card shown in the chat for a generated artifact (document, podcast, …).
const ChatMessageArtifactCard = React.forwardRef<HTMLDivElement, ChatMessageArtifactCardProps>(({
  title,
  subtitle,
  onClick,
  testId,
  isActive = false,
  variant = CHAT_MESSAGE_ARTIFACT_CARD_VARIANT.DOCUMENT,
}, ref) => {
  const { icon, defaultTestId, iconSize, boxSize } = VARIANT_CONFIG[variant];

  return (
    <Box
      ref={ref}
      onClick={onClick}
      data-testid={testId ?? defaultTestId}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: spaces.xs,
        padding: spaces.sm,
        width: '100%',
        backgroundColor: palette.background.paperElevation2,
        outline: isActive ? `2px solid ${palette.primary.main}` : 'none',
        borderRadius: borderRadius.md,
        cursor: onClick ? 'pointer' : 'default',
        // Only the hover shadow transitions; the active outline (palette.primary.main) applies immediately.
        transition: 'box-shadow 0.2s ease-in-out',
        boxSizing: 'border-box',
        boxShadow: boxShadows.elevation1,
        '&:hover': onClick ? {
          boxShadow: boxShadows.elevation2,
        } : {},
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingRight: spaces.xxs,
        }}
      >
        <Avatar
          sx={{
            width: boxSize,
            height: boxSize,
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SvgIcon
            component={getMuiIcon(icon)}
            sx={{
              color: palette.text.secondary,
              fontSize: iconSize,
            }}
          />
        </Avatar>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        <Typography
          variant='subtitle1'
          sx={{
            fontWeight: 500,
            lineHeight: '28px',
            width: '100%',
            color: palette.text.primary,
            ...getTextTruncationSx(1),
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant='caption'
            sx={{
              color: palette.text.disabled,
              width: '100%',
              ...getTextTruncationSx(1),
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

export default ChatMessageArtifactCard;
